export const TRACKING_PROTOCOL_VERSION = 1 as const
export const TRACKING_MESSAGE_KEY = 'GRILLD_TRACKING_BRIDGE' as const

export type CanonicalActionSource = 'web' | 'app_ios' | 'app_android'
export type CanonicalEntryMode = 'embedded' | 'standalone' | 'native'
export type TrackingConsentState = 'granted' | 'denied' | 'not_implemented'

export interface CanonicalTrackingItem {
  item_id: string
  item_name?: string
  price?: number
  quantity?: number
  item_category?: string
}

export interface CanonicalTrackingEvent {
  event_name: string
  event_id: string
  event_time: number
  environment: string
  action_source: CanonicalActionSource
  entry_mode: CanonicalEntryMode
  journey_id?: string
  page_url?: string
  top_level_page_url?: string
  referrer?: string
  value?: number
  currency?: string
  order_id?: string
  items?: CanonicalTrackingItem[]
  attribution?: {
    source?: string
    medium?: string
    campaign?: string
    content?: string
    term?: string
    meta_click_id?: string
    pinterest_click_id?: string
    reddit_click_id?: string
    snapchat_click_id?: string
    tiktok_click_id?: string
    captured_at?: number
    last_activity_at?: number
  }
  user_data?: {
    email_sha256?: string
    phone_sha256?: string
    external_id_sha256?: string
    advertising_id?: string
  }
  consent: {
    analytics: TrackingConsentState
    advertising: TrackingConsentState
    att_status?: string
  }
  parameters?: Record<string, unknown>
}

interface TrackingMessageBase<T extends string> {
  key: typeof TRACKING_MESSAGE_KEY
  version: typeof TRACKING_PROTOCOL_VERSION
  type: T
}

export interface TrackingReadyMessage extends TrackingMessageBase<'ready'> {
  message_id: string
}

export interface TrackingInitMessage extends TrackingMessageBase<'init'> {
  message_id: string
  journey_id: string
  environment: string
}

export interface TrackingEventMessage extends TrackingMessageBase<'event'> {
  event: CanonicalTrackingEvent
}

export interface TrackingAckMessage extends TrackingMessageBase<'ack'> {
  event_id: string
}

export type TrackingBridgeMessage =
  | TrackingReadyMessage
  | TrackingInitMessage
  | TrackingEventMessage
  | TrackingAckMessage

export function isTrackingBridgeMessage(value: unknown): value is TrackingBridgeMessage {
  if (!value || typeof value !== 'object') return false

  const message = value as Record<string, unknown>
  if (message.key !== TRACKING_MESSAGE_KEY || message.version !== TRACKING_PROTOCOL_VERSION) return false

  switch (message.type) {
    case 'ready':
      return typeof message.message_id === 'string'
    case 'init':
      return (
        typeof message.message_id === 'string' &&
        typeof message.journey_id === 'string' &&
        typeof message.environment === 'string'
      )
    case 'event':
      return isCanonicalTrackingEvent(message.event)
    case 'ack':
      return typeof message.event_id === 'string'
    default:
      return false
  }
}

export function isCanonicalTrackingEvent(value: unknown): value is CanonicalTrackingEvent {
  if (!value || typeof value !== 'object') return false

  const event = value as Record<string, unknown>
  return (
    typeof event.event_name === 'string' &&
    typeof event.event_id === 'string' &&
    typeof event.event_time === 'number' &&
    typeof event.environment === 'string' &&
    ['web', 'app_ios', 'app_android'].includes(String(event.action_source)) &&
    ['embedded', 'standalone', 'native'].includes(String(event.entry_mode)) &&
    !!event.consent &&
    typeof event.consent === 'object'
  )
}
